import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ClicktermDialog } from '@clickterm/widget';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../environments/environment';
import { BillingCountry } from '../../shared/models/billing-country.model';
import {
  RegistrationByInviteModel,
  RegistrationCompanyBillingModel,
  RegistrationCompanyBillingResponseModel,
  RegistrationMainInfoModel,
  SubaccountInviteResponseModel,
} from '../../shared/models/user.model';
import { BrowserUtils } from '../../shared/services/browser-utils';
import { ClicktermService } from '../../shared/services/clickterm.service';
import { BillingCountryService } from '../../shared/services/billing-country.service';
import { LegalDocumentsService } from '../../shared/services/legal-documents.service';
import { NotificationService } from '../../shared/services/notification.service';
import { UsersService } from '../../shared/services/users.service';
import { ValidationService, Validators as Vld } from '../../shared/services/validation.service';

type RegistrationStep = 'main-info' | 'creating' | 'company-billing' | 'success' | 'invalid-invite';

@Component({
  standalone: false,
  selector: 'app-registration-form',
  templateUrl: './registration-form.component.html',
  styleUrls: ['./registration-form.component.scss'],
})
export class RegistrationFormComponent implements OnInit, OnDestroy {
  private readonly continuationTokenKey = 'registrationContinuationToken';
  private readonly euCountryCodes = new Set([
    'AT',
    'BE',
    'BG',
    'HR',
    'CY',
    'CZ',
    'DK',
    'EE',
    'FI',
    'FR',
    'DE',
    'GR',
    'HU',
    'IE',
    'IT',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SK',
    'SI',
    'ES',
    'SE',
  ]);

  invite = null;
  loading = false;
  passwordType = 'password';
  step: RegistrationStep;

  model: RegistrationMainInfoModel = {
    username: null,
    password: null,
    fullName: null,
    websiteUrl: null,
    email: null,
    phone: null,
    source: null,
    recaptchaToken: null,
    clicktermSignature: null,
    clicktermUUID: null,
  };

  companyBillingModel: RegistrationCompanyBillingModel = this.getBlankCompanyBillingModel();
  inviteCountryCode = '';

  form: FormGroup;
  companyBillingForm: FormGroup;
  countries: BillingCountry[] = [];

  recaptchaErrorSubscription: Subscription;
  private templateIdForRegistration: string;

  constructor(
    public billingCountryService: BillingCountryService,
    public userService: UsersService,
    private formBuilder: FormBuilder,
    public notificationService: NotificationService,
    public router: Router,
    public validationService: ValidationService,
    private route: ActivatedRoute,
    private recaptchaV3Service: ReCaptchaV3Service,
    private legalDocumentsService: LegalDocumentsService,
    private clicktermService: ClicktermService
  ) {}

  ngOnInit() {
    this.initializeFormAndControls();
    this.initializeClickterm();
    this.getTemplateIdForRegistration();
    this.handleRecaptchaErrors();
    this.loadCountries();
    this.handleRouteParams();
  }

  initializeFormAndControls(): void {
    this.form = this.formBuilder.group({
      websiteUrl: ['', Vld.compose([Vld.required, Vld.url(true), Vld.maxLength(100)])],
      source: [''],
      email: ['', Vld.compose([Vld.required, Vld.email(true)])],
      fullName: ['', Vld.compose([Vld.required, Vld.maxLength(255)])],
      username: ['', Vld.compose([Vld.required, Vld.username(true)])],
      phone: ['', Vld.compose([Vld.required, Vld.globalPhoneNumber()])],
      countryCode: [''],
      password: ['', Vld.compose([Vld.required, Vld.minLength(8)]), [Vld.passwordBackend(this.userService)]],
    });

    this.companyBillingForm = this.formBuilder.group({
      companyName: ['', Vld.compose([Vld.required, Vld.maxLength(255)])],
      countryCode: ['', Vld.compose([Vld.required])],
      addressState: ['', Vld.compose([Vld.notSpacesOnly(), Vld.minLength(1), Vld.maxLength(100)])],
      addressCity: ['', Vld.compose([Vld.required, Vld.notSpacesOnly(), Vld.minLength(3), Vld.maxLength(100)])],
      addressStreet: ['', Vld.compose([Vld.required, Vld.notSpacesOnly(), Vld.minLength(1), Vld.maxLength(255)])],
      addressZip: ['', Vld.compose([Vld.notSpacesOnly(), Vld.minLength(3), Vld.maxLength(20)])],
      vatNumber: ['', Vld.compose([Vld.notSpacesOnly(), Vld.minLength(3), Vld.maxLength(20)])],
    });
  }

  getTemplateIdForRegistration(): void {
    this.legalDocumentsService.getTemplateIDForRegistration().subscribe({
      next: (templateId: string) => {
        this.templateIdForRegistration = templateId;
      },
      error: (error: unknown) => {
        console.error('Failed to get Clickterm template ID for registration: ', error);
      },
    });
  }

  initializeClickterm(): void {
    this.clicktermService.initialize();
  }

  handleRecaptchaErrors(): void {
    this.recaptchaErrorSubscription = this.recaptchaV3Service.onExecuteError.subscribe(error => {
      console.error('Error occurred during reCAPTCHA execution: ', error);
      this.notificationService.error('There is a problem with the registration. Please contact support.', 'Registration');
    });
  }

  loadCountries(): void {
    this.billingCountryService.all().subscribe({
      next: countries => {
        this.countries = [...countries].sort((a, b) => a.name.localeCompare(b.name));
      },
      error: err => {
        console.log(err);
      },
    });
  }

  handleRouteParams(): void {
    this.route.queryParams.subscribe(params => {
      if (params.invite) {
        this.invite = params.invite;
        this.configureInviteForm();
        this.userService
          .validateInvite(params.invite)
          .pipe(
            catchError(e => {
              this.step = 'main-info';
              throw e;
            })
          )
          .subscribe(valid => (this.step = valid ? 'main-info' : 'invalid-invite'));
      } else {
        this.configureMainInfoForm();
        this.step = this.router.url.includes('/registration/company-billing') && this.getContinuationToken() ? 'company-billing' : 'main-info';
        if (this.step === 'company-billing') {
          this.resetCompanyBillingForm();
        }
      }
    });
  }

  togglePasswordType() {
    this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
  }

  isPasswordType() {
    return this.passwordType === 'password';
  }

  onClearUsername() {
    this.form.get('username').setValue('');
  }

  onSubmit() {
    this.recaptchaV3Service.execute('importantAction').subscribe({
      next: (token: string) => this.handleRecaptcha(token),
      error: (error: unknown) => {
        console.error('ReCaptcha failed: ', error);
        this.notificationService.error('There is a problem with the registration. Please contact support.', 'Registration');
      },
    });
  }

  onCompanyBillingSubmit() {
    const continuationToken = this.getContinuationToken();
    if (!continuationToken) {
      this.notificationService.error('Please create your account before submitting company details.', 'Registration');
      void this.router.navigate(['/registration']);
      this.step = 'main-info';
      return;
    }

    this.loading = true;
    const model: RegistrationCompanyBillingModel = {
      ...this.companyBillingModel,
      countryName: this.countryNameFor(this.companyBillingModel.countryCode),
      continuationToken,
      addressState: this.emptyToNull(this.companyBillingModel.addressState),
      addressZip: this.emptyToNull(this.companyBillingModel.addressZip),
      vatNumber: this.emptyToNull(this.companyBillingModel.vatNumber),
      queryString: BrowserUtils.getCookie('telqteleParams') || '',
    };

    this.userService.submitRegistrationCompanyBilling(model).subscribe({
      next: response => {
        this.showVatResult(response);
        this.loading = false;
        this.clearContinuationToken();
        this.step = 'success';
        window.history.pushState({}, null as string, '/registration/thankyou');
      },
      error: e => {
        this.validationService.handleRequestError(e, 'Registration');
        this.loading = false;
      },
    });
  }

  isBillingCountryEu(): boolean {
    return this.euCountryCodes.has(this.companyBillingModel.countryCode);
  }

  isBillingCountryNonEu(): boolean {
    return !!this.companyBillingModel.countryCode && !this.isBillingCountryEu();
  }

  getVatHelperText(): string {
    if (this.isBillingCountryEu()) {
      return (
        "If you are VAT registered, please provide your VAT number. If you don't know it, leave this field empty. " +
        'If you do not have a VAT number, please note that 19% VAT will be applied to every purchase you make.'
      );
    }

    return '';
  }

  getVatTooltipText(): string {
    return this.isBillingCountryNonEu()
      ? 'VAT number is optional for non-EU companies. If provided, it will be shown on invoices where applicable.'
      : '';
  }

  private showVatResult(response: RegistrationCompanyBillingResponseModel): void {
    if (!response?.message) {
      return;
    }

    if (response.vatStatus === 'UNVERIFIED' || response.vatStatus === 'VIES_UNAVAILABLE') {
      this.notificationService.warning(response.message, 'Registration');
    } else {
      this.notificationService.success(response.message, 'Registration');
    }
  }

  private handleRecaptcha(token: string) {
    this.loading = true;
    if (!this.invite) {
      const model: RegistrationMainInfoModel = {
        ...this.model,
        source: this.model.source === '' ? null : this.model.source,
        recaptchaToken: token,
        clicktermUUID: uuidv4(),
      };
      this.model = model;

      if (!this.clicktermService.initialize()) {
        console.error('Clickterm dialog not initialized.');
        this.notificationService.error('There is a problem with the registration. Please contact support.', 'Registration');
        this.loading = false;
        return;
      }

      this.userService
        .validateRegistrationMainInfo({
          ...model,
          clicktermSignature: 'empty',
        })
        .subscribe({
          next: () => {
            this.showClicktermDialog(model);
          },
          error: e => {
            this.handleRegistrationError(e);
            this.loading = false;
          },
        });
    } else {
      const modelByInvite: RegistrationByInviteModel = {
        username: this.model.username,
        password: this.model.password,
        fullName: this.model.fullName,
        phone: this.model.phone,
        countryName: this.countryNameFor(this.inviteCountryCode),
        countryCode: this.inviteCountryCode,
      };
      this.registerByInvite(modelByInvite);
    }
  }

  private registerMainInfo(model: RegistrationMainInfoModel) {
    this.step = 'creating';
    this.userService.registrationMainInfo(model).subscribe({
      next: response => {
        this.loading = false;
        this.storeContinuationToken(response.continuationToken);
        this.resetCompanyBillingForm();
        this.step = 'company-billing';
        void this.router.navigate(['/registration/company-billing']);
      },
      error: e => {
        this.handleRegistrationError(e);
        this.loading = false;
        this.step = 'main-info';
      },
    });
  }

  private registerByInvite(modelByInvite: RegistrationByInviteModel) {
    this.userService.registrationByInvite(modelByInvite, this.invite, BrowserUtils.getCookie('telqteleParams') || '').subscribe({
      next: (res: SubaccountInviteResponseModel) => {
        console.debug('Registration by invite success : ', this.invite);
        modelByInvite.email = res.email;
        this.loading = false;
        this.step = 'success';
        window.history.pushState({}, null as string, 'registration/thankyou');
      },
      error: e => {
        this.validationService.handleRequestError(e, 'Registration');
        this.loading = false;
      },
    });
  }

  private getDomainError(email: string): string {
    const emailDomain = email.split('@')[1];
    return `The domain name ${emailDomain} cannot be used to register on our testing platform. Please use your corporate email instead.<br>Note:  We only allow accounts to be created with a corporate email (free domain emails such as gmail or disposable emails will not be accepted)`;
  }

  ngOnDestroy() {
    this.recaptchaErrorSubscription?.unsubscribe();
  }

  private handleRegistrationError(error): void {
    if (error.statusText && error.statusText.indexOf('Duplicate entry') !== -1) {
      this.notificationService.error('Registration failed. Please contact our support.', 'Registration');
    } else if (error.statusText && error.statusText.indexOf('com.telqtele.api.gateway.InvalidEmail') !== -1) {
      let message = error.statusText.replace('com.telqtele.api.gateway.InvalidEmail: ', '');
      if (message.includes('Disposable address.')) {
        message = this.getDomainError(this.model.email);
      }
      this.notificationService.error(message, 'Registration');
    } else if (error.statusText && error.statusText.includes('com.telqtele.api.gateway.InvalidRequestException')) {
      const message = this.getDomainError(this.model.email);
      this.notificationService.error(message, 'Registration');
    } else {
      this.validationService.handleRequestError(error, 'Registration');
    }
  }

  private showClicktermDialog(model: RegistrationMainInfoModel) {
    ClicktermDialog.show(
      {
        endUserId: model.clicktermUUID,
        clickwrapTemplateId: this.templateIdForRegistration ?? environment.clicktermTemplateId,
        templatePlaceholders: {
          fullName: model.fullName,
          email: model.email,
          phoneNumber: model.phone,
        },
        language: 'en',
      },
      { disableClose: true }
    )
      .then(clicktermResponse => {
        const signature = clicktermResponse?.clicktermSignature;
        if (!signature) {
          throw new Error('Clickterm signature missing in response.');
        }
        model.clicktermSignature = signature;
        this.registerMainInfo(model);
      })
      .catch((error: unknown) => {
        console.error('Clickterm error:', error);
        this.notificationService.error('There is a problem with the registration. Please contact support.', 'Registration');
        this.loading = false;
      });
  }

  private configureMainInfoForm(): void {
    this.setValidators('websiteUrl', Vld.compose([Vld.required, Vld.url(true), Vld.maxLength(100)]));
    this.setValidators('email', Vld.compose([Vld.required, Vld.email(true)]));
    this.setValidators('source', null);
    this.setValidators('countryCode', null);
    this.form.updateValueAndValidity();
  }

  private configureInviteForm(): void {
    this.setValidators('websiteUrl', null);
    this.setValidators('email', null);
    this.setValidators('source', null);
    this.setValidators('countryCode', Vld.compose([Vld.required]));
    this.form.updateValueAndValidity();
  }

  private setValidators(controlName: string, validators: ValidatorFn | ValidatorFn[] | null): void {
    const control = this.form.get(controlName);
    control.setValidators(validators);
    control.updateValueAndValidity();
  }

  private getBlankCompanyBillingModel(): RegistrationCompanyBillingModel {
    return {
      continuationToken: null,
      companyName: null,
      countryName: '',
      countryCode: '',
      addressState: null,
      addressCity: null,
      addressStreet: null,
      addressZip: null,
      vatNumber: null,
    };
  }

  private resetCompanyBillingForm(): void {
    this.companyBillingModel = this.getBlankCompanyBillingModel();
    this.companyBillingForm.reset({
      companyName: null,
      countryCode: '',
      addressState: null,
      addressCity: null,
      addressStreet: null,
      addressZip: null,
      vatNumber: null,
    });
  }

  private storeContinuationToken(token: string): void {
    sessionStorage.setItem(this.continuationTokenKey, token);
  }

  private getContinuationToken(): string | null {
    return sessionStorage.getItem(this.continuationTokenKey);
  }

  private clearContinuationToken(): void {
    sessionStorage.removeItem(this.continuationTokenKey);
  }

  private emptyToNull(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }

  private countryNameFor(code: string): string {
    return this.countries.find(country => country.code === code)?.name ?? '';
  }
}
